import React from "react";

interface Props {
  headerLeft?: React.ReactNode;
  /** "N activities" shown beside the title (left group). */
  activityCount?: number;
  mode: "board" | "live";
  onModeChange?: (m: "board" | "live") => void;
  /** Note under the toolbar (full-width sub-line). */
  modeNote?: string;
  /** "Apply dates" action. Only clickable in Forward-pass mode (there are no
   *  computed dates otherwise). */
  onApplyDates?: () => void;
  applyDatesLabel?: string;
  applyDatesNote?: string;
  /** true ⇒ the board doesn't match the computed dates yet, so the button lights
   *  up green; false ⇒ nothing to apply, so it's disabled + clearly greyed.
   *  Undefined leaves the legacy "enabled when in Forward pass" behavior. */
  applyDatesActive?: boolean;
  /** "Driving only" chart toggle, surfaced as a pill in the toolbar (the dep
   *  modal, which has no board to apply dates to). Provided ⇒ the pill renders
   *  in the Apply-dates slot. Omitted ⇒ the toggle stays on the chart's ⚙ gear. */
  drivingOnly?: boolean;
  onToggleDrivingOnly?: () => void;
  /** Row order: "zone" = grouped by section/level, "date" = flat chronological
   *  (best for cross-section ties). The "By date" toggle shares ONE scope slot
   *  with "Show all": focused → Show all (un-focus); all levels → By date. */
  viewMode?: "zone" | "date";
  onViewModeChange?: (m: "zone" | "date") => void;
  focused?: boolean;
  onShowAll?: () => void;
  driverCount: number;
  showDrivers: boolean;
  onToggleShowDrivers: () => void;
  /** Full-screen the logic editor over the whole app (header included). */
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

const SequenceModeBar: React.FC<Props> = ({
  headerLeft,
  activityCount,
  mode,
  onModeChange,
  modeNote,
  onApplyDates,
  applyDatesLabel,
  applyDatesNote,
  applyDatesActive,
  drivingOnly,
  onToggleDrivingOnly,
  viewMode,
  onViewModeChange,
  focused,
  onShowAll,
  driverCount,
  showDrivers,
  onToggleShowDrivers,
  fullscreen,
  onToggleFullscreen,
}) => (
  <div className="sequence-view-modes">
    <div className="sequence-view-modes-row">
      <div className="sequence-view-modes-side sequence-view-modes-side--left">
        {headerLeft}
        {activityCount != null && (
          <span className="sequence-view-actcount">
            {activityCount} {activityCount === 1 ? "activity" : "activities"}
          </span>
        )}
      </div>

      <span className="sequence-view-modes-grow" />

      <div className="sequence-view-modes-side sequence-view-modes-side--right">
        {/* ONE scope slot: focused → Show all (un-focus); all levels → By date. */}
        {focused && onShowAll ? (
          <button
            type="button"
            className="sequence-view-scope-btn sequence-view-ext-toggle"
            title="Show every level's dependencies (stop focusing this one)"
            onClick={onShowAll}
          >
            Show all
          </button>
        ) : onViewModeChange ? (
          <button
            type="button"
            className={
              "sequence-view-scope-btn sequence-view-ext-toggle" +
              (viewMode === "date" ? " is-active" : "")
            }
            title="Order ties chronologically across all sections, with no level groups — best for spotting cross-section ties"
            aria-pressed={viewMode === "date"}
            onClick={() => onViewModeChange(viewMode === "date" ? "zone" : "date")}
          >
            📅 By date
          </button>
        ) : null}

        {onModeChange && (
          <div className="sequence-view-modeseg" role="group" aria-label="Date mode">
            <button
              type="button"
              className={
                "sequence-view-mode-btn" + (mode === "board" ? " is-active" : "")
              }
              title="Show every activity at its BOARD dates — logic is visible but doesn't move anything"
              onClick={() => onModeChange("board")}
            >
              Board dates
            </button>
            <button
              type="button"
              className={
                "sequence-view-mode-btn" + (mode === "live" ? " is-active" : "")
              }
              title="Forward pass: dates recalculate automatically as you edit ties and durations"
              onClick={() => onModeChange("live")}
            >
              Forward pass
            </button>
          </div>
        )}

        {onApplyDates && (
          <button
            type="button"
            className={
              "sequence-view-ext-toggle pull-plan-grid-applydates" +
              (mode === "live" && applyDatesActive === true ? " is-ready" : "")
            }
            disabled={mode !== "live" || applyDatesActive === false}
            title={
              mode !== "live"
                ? "Switch to Forward pass to compute dates first"
                : applyDatesActive === false
                  ? "The board already matches the computed dates — nothing to apply"
                  : (applyDatesNote ||
                      "Take the dependency-driven dates computed here and arrange the bars to match. One undoable history entry.")
            }
            onClick={onApplyDates}
          >
            {applyDatesLabel}
          </button>
        )}

        {onToggleDrivingOnly && (
          <button
            type="button"
            className={
              "sequence-view-ext-toggle" + (drivingOnly ? " is-active" : "")
            }
            title="On the gantt, draw only the predecessor line that actually sets each activity's date; hide the slack ones."
            aria-pressed={drivingOnly}
            onClick={onToggleDrivingOnly}
          >
            Driving only
          </button>
        )}

        {driverCount > 0 && (
          <button
            type="button"
            className={
              "sequence-view-ext-toggle sequence-view-ext-ties" +
              (showDrivers ? " is-active" : "")
            }
            title="Show ties from outside this section — out-of-section & finished-work ties as mini-bars on the rows they drive"
            onClick={onToggleShowDrivers}
            aria-pressed={showDrivers}
          >
            External ties ({driverCount})
          </button>
        )}

        {onToggleFullscreen && (
          <button
            type="button"
            className={
              "sequence-view-ext-toggle sequence-view-fs-btn" +
              (fullscreen ? " is-active" : "")
            }
            title={fullscreen ? "Exit full screen" : "Full screen the logic editor"}
            aria-pressed={fullscreen}
            onClick={onToggleFullscreen}
          >
            {fullscreen ? "⤡" : "⤢"}
          </button>
        )}
      </div>
    </div>

    {mode === "live" && modeNote && (
      <div className="sequence-view-mode-subline">{modeNote}</div>
    )}
  </div>
);

export default SequenceModeBar;
